<script>
import useValidation from "@/use/Validation.js";
import useSharedData from "@/use/SharedData.js";
import useTools from "@/use/Tools.js";
import useSharedMethod from "@/use/SharedMethod.js";
import {computed, onMounted, ref} from "vue";
import {useAlarmStore} from "@/views/Notification/stores/Alarm.js";
import useSharedControlProgram from "@/views/Notification/use/SharedControlProgram";
import useSharedAlarm from "@/views/Notification/use/SharedAlarm";
import axios from "axios";

export default {
    name: "AlarmForm",
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },
    
    setup(props) {
        const {mxRules} = useValidation();
        const {loading, dialog, overlay} = useSharedData();
        const {$route, $refs} = useTools();
        const {getBack} = useSharedMethod();
        const {alarmPeriodicity, alarmReceiverTypes, sendingMethods, months, days, monthNumberDays, halfYearlyMonths} = useSharedAlarm();
        
        const {controlModules} = useSharedControlProgram();
        
        const alarmStore = useAlarmStore();
        
        const controlPrograms = ref([]);
        const loadingControlProgram = ref(false);
        
        function findAllControlPrograms() {
            loadingControlProgram.value = true;
            axios.get('/controlPrograms', {
                params: {
                    allRecords: true,
                    isMiddle: false,
                }
            }).then(response => {
                controlPrograms.value = response.data.elements;
            }).finally(() => {
                loadingControlProgram.value = false;
            })
        }
        
        const readonly = computed(() => {
            return props.viewAction || props.deleteAction;
        });
        
        const alarm = ref({
            timeOption: 'once',
            type: 'controlProgram',
            programIds: [],
            times: [],
            actions: ['post', 'update', 'delete', 'patch'],
            receiverUserIds: [],
            sendingMethods: [],
        });
        
        const time = ref(null);
        
        function addTime() {
            if (time) {
                alarm.value.times.push(time.value);
            }
        }
        
        function removeTime(time) {
            let index = alarm.value.times.findIndex(e => e === time);
            alarm.value.times.splice(index, 1);
        }
        
        function findById(id) {
            loading.value = true;
            axios.get(`/alarms/${id}`).then((response) => {
                alarm.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/alarms', alarm.value).then((response) => {
                    alarmStore.addAlarm(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/alarms', alarm.value).then((response) => {
                    alarmStore.updateAlarm(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function remove() {
            loading.value = true;
            axios.delete(`/alarms/${alarm.value.id}`).then(() => {
                alarmStore.deleteAlarm(alarm.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }
        
        onMounted(() => {
            findAllControlPrograms();
            if (!props.addAction)
                findById($route.params.id);
        });
        
        return {
            time,
            alarmPeriodicity,
            alarmReceiverTypes,
            sendingMethods,
            months,
            days,
            monthNumberDays,
            halfYearlyMonths,
            loadingControlProgram,
            controlPrograms,
            controlModules,
            alarm,
            mxRules,
            readonly,
            loading,
            dialog,
            overlay,
            addTime,
            removeTime,
            save,
            update,
            remove,
            getBack,
        };
    },
}
</script>

<template>
    <v-dialog
        v-model="dialog"
        persistent
        scrollable
        max-width="800"
    >
        <v-card>
            <w-dialog-toolbar
                :view-action="viewAction"
                :delete-action="deleteAction"
                :update-action="updateAction"
                :add-action="addAction"
            />
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @cancel="overlay = false"
                @remove="remove"
            />
            <v-card-text>
                <v-form lazy-validation ref="form">
                    <v-container fluid>
                        <w-fieldset :title="$t('common.details')">
                            <v-row dense>
                                <v-col cols="12" md="6">
                                    <w-label :label="$t('common.name')" required/>
                                    <w-text-field
                                        v-model="alarm.name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <w-label :label="$t('common.comment')"/>
                                    <w-text-area
                                        v-model="alarm.description"
                                        :readonly="readonly"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset :title="$t('alarm.source')">
                            <v-row dense>
                                <v-col cols="12">
                                    <v-radio-group
                                        row
                                        v-model="alarm.type"
                                        :readonly="readonly"
                                    >
                                        <v-radio
                                            :label="$t('module.control_program')"
                                            value="controlProgram"
                                        />
                                        <v-radio
                                            :label="$t('common.module')"
                                            value="module"
                                        />
                                    </v-radio-group>
                                </v-col>
                            </v-row>
                            <v-row dense v-if="alarm.type === 'module'">
                                <v-col cols="12">
                                    <w-label :label="$t('common.module')" required/>
                                    <w-enum-select
                                        v-model="alarm.modules"
                                        :items="controlModules"
                                        multiple
                                        :readonly="readonly"
                                        :rules="[mxRules.listRequired]"
                                    />
                                </v-col>
                                <v-col cols="3">
                                    <v-checkbox
                                        v-model="alarm.actions"
                                        :label="$t('common.addition')"
                                        value="post"
                                        color="teal"
                                        :readonly="readonly"
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="3">
                                    <v-checkbox
                                        v-model="alarm.actions"
                                        :label="$t('common.modification')"
                                        value="update"
                                        color="orange"
                                        :readonly="readonly"
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="3">
                                    <v-checkbox
                                        v-model="alarm.actions"
                                        :label="$t('common.deletion')"
                                        value="delete"
                                        color="red"
                                        :readonly="readonly"
                                        hide-details
                                    />
                                </v-col>
                            </v-row>
                            <v-row dense v-if="alarm.type === 'controlProgram'">
                                <v-col cols="12">
                                    <w-label :label="$t('module.control_program')" required/>
                                    <w-enum-select
                                        v-model="alarm.programIds"
                                        :items="controlPrograms"
                                        :loading="loadingControlProgram"
                                        multiple
                                        :readonly="readonly"
                                        :rules="[mxRules.listRequired]"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset :title="$t('alarm.treatment')">
                            <v-row dense>
                                <v-col cols="6">
                                    <w-label :label="$t('alarm.send_on')" required/>
                                    <w-enum-select
                                        v-model="alarm.sendingMethods"
                                        :items="sendingMethods"
                                        multiple
                                        :readonly="readonly"
                                        :rules="[mxRules.listRequired]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.periodicity')" required/>
                                    <w-enum-select
                                        v-model="alarm.periodicity"
                                        :items="alarmPeriodicity"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                            <v-row dense v-if="alarm.periodicity !== 'only_once'">
                                <v-col
                                    v-if="alarm.periodicity === 'daily'"
                                    cols="12"
                                >
                                    <w-label :label="$t('common.days')" required/>
                                    <w-enum-select
                                        v-model="alarm.daysOfWeek"
                                        :items="days"
                                        multiple
                                        :readonly="readonly"
                                        :rules="[mxRules.listRequired]"
                                    />
                                </v-col>
                                <v-col
                                    v-if="alarm.periodicity === 'weekly'"
                                    cols="12"
                                >
                                    <w-label :label="$t('common.day')" required/>
                                    <w-enum-select
                                        v-model="alarm.dayOfWeek"
                                        :items="days"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col
                                    v-if="alarm.periodicity === 'monthly'"
                                    cols="12"
                                >
                                    <w-label :label="$t('common.day_of_month')" required/>
                                    <w-enum-select
                                        v-model="alarm.dayOfMonth"
                                        :items="monthNumberDays"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col
                                    v-if="alarm.periodicity === 'halfYearly'"
                                    cols="12"
                                >
                                    <w-label :label="$t('common.month_half_year')" required/>
                                    <w-enum-select
                                        v-model="alarm.monthOfHalfYear"
                                        :items="halfYearlyMonths"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col
                                    v-if="alarm.periodicity === 'halfYearly'"
                                    cols="12"
                                >
                                    <w-label :label="$t('common.day_of_month')" required/>
                                    <w-enum-select
                                        v-model="alarm.dayOfMonth"
                                        :items="monthNumberDays"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col
                                    v-if="alarm.periodicity === 'yearly'"
                                    cols="12"
                                >
                                    <w-label :label="$t('common.month')" required/>
                                    <w-enum-select
                                        v-model="alarm.month"
                                        :items="months"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col
                                    v-if="alarm.periodicity === 'yearly'"
                                    cols="12"
                                >
                                    <w-label :label="$t('common.day_of_month')" required/>
                                    <w-enum-select
                                        v-model="alarm.dayOfMonth"
                                        :items="monthNumberDays"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                            <v-row dense v-if="alarm.periodicity !== 'only_once'">
                                <v-col cols="12">
                                    <v-radio-group
                                        v-model="alarm.timeOption"
                                        row
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    >
                                        <v-radio
                                            :label="$t('alarm.once')"
                                            value="once"
                                        />
                                        <v-radio
                                            :label="$t('alarm.repeated')"
                                            value="repeated"
                                        />
                                        <v-radio
                                            :label="$t('alarm.list')"
                                            value="list"
                                        />
                                    </v-radio-group>
                                </v-col>
                                <v-col cols="12" v-if="alarm.timeOption === 'repeated'">
                                    <w-text-field
                                        v-model="alarm.repeatInMinutes"
                                        type="number"
                                        :suffix="$t('common.minutes')"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6" v-if="alarm.timeOption === 'list' && !readonly">
                                    <w-time-picker
                                        v-model="time"
                                        :label="$t('common.time')"
                                        color="cyan"
                                    />
                                </v-col>
                                <v-col cols="6" v-if="alarm.timeOption === 'list' && !readonly">
                                    <w-btn
                                        icon="mdi-plus-circle-outline"
                                        :text="$t('common.add')"
                                        color="cyan"
                                        block
                                        medium
                                        @press="addTime"
                                    />
                                </v-col>
                                <v-col cols="12" v-if="alarm.timeOption === 'list'">
                                    <v-chip-group column>
                                        <v-chip
                                            color="cyan"
                                            class="white--text"
                                            :close="!readonly"
                                            v-for="(t, index) in alarm.times"
                                            :key="index"
                                            @click:close="removeTime(t)"
                                        >
                                            <v-icon left>mdi-alarm</v-icon>
                                            {{ t }}
                                        </v-chip>
                                    </v-chip-group>
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset :title="$t('alarm.assignment')">
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('alarm.assignment')" required/>
                                    <w-enum-select
                                        v-model="alarm.alarmReceiverType"
                                        :items="alarmReceiverTypes"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" v-if="alarm.alarmReceiverType === 'SPECIFIC'">
                                    <w-label :label="$t('module.worker')" required/>
                                    <str-worker-autocomplete
                                        v-model="alarm.receiverIds"
                                        :readonly="readonly"
                                        multiple
                                        :rules="[mxRules.listRequired]"
                                    />
                                </v-col>
                                <v-col cols="12" v-if="alarm.alarmReceiverType === 'SERVICE'">
                                    <w-label :label="$t('alarm.SERVICE')" required/>
                                    <organizational-chart-field
                                        v-model="alarm.organizationalChartId"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    ></organizational-chart-field>
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>

</style>